import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssignmentSectionQuery, AssignmentSectionStore } from '@examdojo/assignment';
import {
  ChallengeActivityHttpModel,
  ChallengeActivityQuery,
  ChallengeActivityService,
  ChallengeActivityStore,
} from '@examdojo/challenge';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { UserQuery } from '@examdojo/user';
import { concatMap, EMPTY, filter, map, merge, of, switchMap, withLatestFrom } from 'rxjs';
import { ExamdojoPlatformService } from '../../../shared/platform.service';
import { ChallengeDialogService } from '../dialog/challenge-dialog.service';

@Injectable()
export class ChallengeActivityRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly userQuery: UserQuery,
    private readonly store: ChallengeActivityStore,
    private readonly query: ChallengeActivityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly assignmentSectionQuery: AssignmentSectionQuery,
    private readonly assignmentSectionStore: AssignmentSectionStore,
    private readonly challengeDialogService: ChallengeDialogService,
  ) {
    merge(this.listenForChallengeActivityStatusChange()).pipe(takeUntilDestroyed()).subscribe();
  }

  private listenForChallengeActivityStatusChange() {
    const isChallengeEnabled = this.featureFlagService.get(ExamDojoFeatureFlag.Exams);
    if (!isChallengeEnabled) {
      return EMPTY;
    }

    return this.userQuery.active$.pipe(
      filter(Boolean),
      switchMap((user) =>
        this.realtimeService.listenBroadcastRealtime<ChallengeActivityHttpModel>(
          [{ event: RealtimeChangesListenEvent.UPDATE }],
          `challenge_activity_status:${user.id}`,
        ),
      ),
      withLatestFrom(this.platformService.activeStatusState$),
      filter(([_, isActive]) => isActive),
      map(([event]) => event),
      concatMap((payload) => {
        const record = payload?.payload?.record;

        if (!record?.id || payload.event !== RealtimeChangesListenEvent.UPDATE) {
          return of(null);
        }

        this.syncAssignmentSectionOngoingStatus(record);

        const storeChallengeActivity = this.query.getEntity(record.id);

        if (storeChallengeActivity && new Date(storeChallengeActivity.updated_at) >= new Date(record.updated_at)) {
          return of(null);
        }

        if (
          storeChallengeActivity &&
          ((record.status === 'COMPLETED' && storeChallengeActivity.status !== 'COMPLETED') ||
            (record.status === 'DISCARDED' && storeChallengeActivity.status !== 'DISCARDED') ||
            (record.status === 'FAILED' && storeChallengeActivity.status !== 'FAILED'))
        ) {
          return this.handleTerminalChallengeActivityStatusChange(record);
        }

        if (storeChallengeActivity?.status === 'COMPLETED') {
          return of(null);
        }

        if (this.shouldSkipStaleQuestionIdUpdate(storeChallengeActivity, record)) {
          return of(null);
        }

        if (
          !storeChallengeActivity ||
          (storeChallengeActivity && storeChallengeActivity.current_question_id !== record.current_question_id) ||
          (storeChallengeActivity && storeChallengeActivity.status !== record.status)
        ) {
          return this.handleInProgressChallengeActivityStatusChange(record);
        }

        return of(null);
      }),
    );
  }

  private handleInProgressChallengeActivityStatusChange(challengeActivityHttpModel: ChallengeActivityHttpModel) {
    this.store.upsert(challengeActivityHttpModel.id, challengeActivityHttpModel);

    if (!this.challengeDialogService.isOpen()) {
      return of(null);
    }

    if (challengeActivityHttpModel.status === 'SUBMITTED') {
      return this.challengeActivityService.syncPostSubmittedChallengeActivity(challengeActivityHttpModel.id);
    }

    if (challengeActivityHttpModel.status === 'GRADED') {
      return this.challengeActivityService.syncPostGradedChallengeActivity();
    }
    return of(null);
  }

  private handleTerminalChallengeActivityStatusChange(challengeActivityHttpModel: ChallengeActivityHttpModel) {
    this.store.update(challengeActivityHttpModel.id, challengeActivityHttpModel);

    return of(null);
  }

  private syncAssignmentSectionOngoingStatus(record: ChallengeActivityHttpModel) {
    const matchingSection = this.assignmentSectionQuery.getEntityByPredicate(
      (s) => s.ongoing_challenge_activity_id === record.id,
    );

    if (!matchingSection) {
      return;
    }

    this.assignmentSectionStore.update(matchingSection.id, {
      ongoing_challenge_activity_status: record.status,
    });
  }

  private shouldSkipStaleQuestionIdUpdate(
    storeChallengeActivity: ChallengeActivityHttpModel | null,
    record: ChallengeActivityHttpModel,
  ): boolean {
    if (!storeChallengeActivity || !this.query.isNavigating()) {
      return false;
    }

    const isOnlyQuestionIdChange =
      storeChallengeActivity.current_question_id !== record.current_question_id &&
      storeChallengeActivity.status === record.status;

    return isOnlyQuestionIdChange;
  }
}
